import React, { useEffect, useState } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { useDispatch, useSelector } from "react-redux";
import { getuserProductWishlist } from "../features/user/userSlice";
import { addToWishlist } from "../features/products/productSlilce";
import {
  AiOutlineShoppingCart,
} from "react-icons/ai";
import { BsHeart, BsTrash, BsHeartFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ProductCard from "../components/ProductCard";

const Wishlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const wishlistState = useSelector((state) => state?.auth?.wishlist?.wishlist);
  const getTokenFromLocalStorage = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  // Check if user is authenticated by verifying token exists and is likely valid
  const isAuthenticated = () => {
    if (!getTokenFromLocalStorage || !getTokenFromLocalStorage.token) {
      return false;
    }
    // Additional check could be added here for token expiration
    return true;
  };

  useEffect(() => {
    // For logged-in users, fetch wishlist from backend
    if (isAuthenticated()) {
      dispatch(getuserProductWishlist());
    } else {
      // For guest users, fetch wishlist from localStorage
      const guestWishlist = JSON.parse(localStorage.getItem("guestWishlist")) || [];
      // In a real application, you would fetch product details based on IDs
      // For now, we'll just set an empty array
      setWishlist([]);
    }
  }, [dispatch]);

  useEffect(() => {
    if (wishlistState) {
      setWishlist(wishlistState);
    }
  }, [wishlistState]);

  const handleRemoveFromWishlist = async (productId) => {
    setLoading(true);
    try {
      await dispatch(addToWishlist(productId)).unwrap();
      dispatch(getuserProductWishlist());
      toast.success("Product removed from wishlist!");
    } catch (error) {
      // Error is handled in the service layer
      toast.error("Failed to remove product from wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    if (isAuthenticated()) {
      // For logged-in users, implement clear all functionality
      toast.info("Clear all wishlist functionality coming soon!");
    } else {
      // For guest users, clear localStorage
      localStorage.removeItem("guestWishlist");
      setWishlist([]);
      toast.success("Wishlist cleared!");
    }
  };

  // For guest users, we need to get product details from the wishlist IDs
  // This is a simplified implementation - in a real app, you would fetch product details
  const guestWishlistIds = JSON.parse(localStorage.getItem("guestWishlist")) || [];

  const isEmpty = isAuthenticated() 
    ? (!wishlist || wishlist.length === 0) 
    : guestWishlistIds.length === 0;

  if (isEmpty) {
    return (
      <>
        <Meta 
          title="My Wishlist - VEDA Merchandise"
          description="View and manage your wishlist at VEDA Merchandise."
          keywords="wishlist, VEDA wishlist, mythological toys wishlist"
          author="VEDA Merchandise"
          robots="noindex, nofollow"
        />
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-red-100 rounded-full mb-6">
                <BsHeart className="text-red-500 text-5xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Your wishlist is empty</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Looks like you haven't added any products to your wishlist yet.
              </p>
              <button
                onClick={() => navigate("/products")}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105"
              >
                <BsHeart className="text-xl" />
                <span>Start Shopping</span>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "My Wishlist - VEDA Merchandise",
    "description": "View and manage your wishlist at VEDA Merchandise.",
    "url": "https://vedamerchandise.com/wishlist",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="My Wishlist - VEDA Merchandise"
        description="View and manage your wishlist at VEDA Merchandise."
        keywords="wishlist, VEDA wishlist, mythological toys wishlist"
        author="VEDA Merchandise"
        ogTitle="My Wishlist - VEDA Merchandise"
        ogDescription="View and manage your wishlist at VEDA Merchandise."
        ogImage="https://vedamerchandise.com/wishlist-preview.jpg"
        ogUrl="https://vedamerchandise.com/wishlist"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="My Wishlist - VEDA Merchandise"
        twitterDescription="View and manage your wishlist at VEDA Merchandise."
        twitterImage="https://vedamerchandise.com/wishlist-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl="https://vedamerchandise.com/wishlist"
        schemaMarkup={schemaMarkup}
      />
      <Container class1="wishlist-wrapper py-5">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
              <div>
                <h3 
                  className="mb-2 my-3 text-xl w-40 bg-yellow-200 text-gray-800 py-2 px-6 rounded-full font-bold"
                >
                  My Wishlist
                </h3>
                <p 
                  style={{
                    fontSize: '15px',
                    color: '#666',
                    margin: 0
                  }}
                >
                  {isAuthenticated() 
                    ? (wishlist?.length || 0) 
                    : guestWishlistIds.length} item{isAuthenticated() 
                      ? (wishlist?.length !== 1 ? "s" : "") 
                      : (guestWishlistIds.length !== 1 ? "s" : "")} saved for later
                </p>
              </div>
              <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
                <button
                  onClick={handleClearAll}
                  disabled={loading}
                  className="btn d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                  style={{
                    backgroundColor: '#fff',
                    color: '#e91e63',
                    border: '2px solid #e91e63',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '700',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fce4ec';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fff';
                  }}
                >
                  <BsTrash style={{ fontSize: '16px' }} />
                  <span>Clear All</span>
                </button>
                <button
                  onClick={() => navigate("/product")}
                  className="btn d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                  style={{
                    backgroundColor: '#e91e63',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '700',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#c2185b';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#e91e63';
                  }}
                >
                  <AiOutlineShoppingCart style={{ fontSize: '18px' }} />
                  <span>Continue Shopping</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Wishlist Items Grid */}
        <div className="row g-3 mb-5">
          {isAuthenticated() ? (
            // For logged-in users, show wishlist from backend
            <ProductCard data={wishlist} grid={3} />
          ) : (
            // For guest users, show a message
            <div className="col-12 text-center py-5">
              <p>You have {guestWishlistIds.length} items in your wishlist.</p>
              <p className="text-muted">Sign up to save your wishlist permanently.</p>
            </div>
          )}
        </div>

        {/* Info Cards Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div 
              className="rounded-4 p-4"
              style={{
                backgroundColor: '#fff',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
              }}
            >
              <div className="row g-4">
                <div className="col-12 col-md-4">
                  <div 
                    className="text-center p-4 rounded-3 h-100"
                    style={{
                      backgroundColor: '#fce4ec'
                    }}
                  >
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <BsHeartFill 
                        style={{ 
                          fontSize: '24px',
                          color: '#e91e63' 
                        }} 
                      />
                    </div>
                    <h3 
                      className="mb-2"
                      style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#333'
                      }}
                    >
                      Save Your Favorites
                    </h3>
                    <p 
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        margin: 0,
                        lineHeight: '1.5'
                      }}
                    >
                      Keep track of products you love and buy them later
                    </p>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div 
                    className="text-center p-4 rounded-3 h-100"
                    style={{
                      backgroundColor: '#fff3e0'
                    }}
                  >
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <AiOutlineShoppingCart 
                        style={{ 
                          fontSize: '24px',
                          color: '#ff9800' 
                        }} 
                      />
                    </div>
                    <h3 
                      className="mb-2"
                      style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#333'
                      }}
                    >
                      Quick Access
                    </h3>
                    <p 
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        margin: 0,
                        lineHeight: '1.5'
                      }}
                    >
                      Add items to cart directly from your wishlist
                    </p>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div 
                    className="text-center p-4 rounded-3 h-100"
                    style={{
                      backgroundColor: '#e8f5e9'
                    }}
                  >
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      <BsHeart 
                        style={{ 
                          fontSize: '24px',
                          color: '#4caf50' 
                        }} 
                      />
                    </div>
                    <h3 
                      className="mb-2"
                      style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        color: '#333'
                      }}
                    >
                      Never Lose Track
                    </h3>
                    <p 
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        margin: 0,
                        lineHeight: '1.5'
                      }}
                    >
                      Your wishlist is saved and synced across devices
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Actions */}
        <div className="row">
          <div className="col-12">
            <div className="text-center">
              <p 
                className="mb-3"
                style={{
                  fontSize: '15px',
                  color: '#666'
                }}
              >
                Looking for more products?
              </p>
              <button
                onClick={() => navigate("/product")}
                className="btn d-inline-flex align-items-center gap-2 px-4 py-3"
                style={{
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#e91e63';
                  e.target.style.color = '#e91e63';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.color = '#333';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                <span>Explore All Products</span>
              </button>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Wishlist;